import {Pencil, Plus} from 'lucide-react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {IconButton} from '@/components/ui/icon-button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemMedia,
} from '@/components/ui/item';
import {TooltipWrapper} from '@/components/ui/tooltip';
import {Typography} from '@/components/ui/typography';
import {graphqlClient} from '@/services/graphqlClient';
import type {JobRole} from '@/services/graphqlHooks';
import {useGetJobRolesQuery} from '@/services/graphqlHooks';
import {localeCompareSort} from '@/utils/localeCompareSort';

import AddJobRoleDialog from './AddJobRoleDialog';
import EditJobRoleDialog from './EditJobRoleDialog';

export type ManageJobRolesDialogProps = DialogProps;

const ManageJobRolesDialog = ({
  onOpenChange,
  ...props
}: ManageJobRolesDialogProps) => {
  const {t} = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [editingJobRole, setEditingJobRole] = useState<JobRole | null>(null);

  const {data} = useGetJobRolesQuery(graphqlClient, undefined, {
    enabled: !!props.open,
  });

  const jobRoles = [...(data?.getJobRoles || [])].sort((roleA, roleB) =>
    localeCompareSort(roleA.name, roleB.name)
  );

  return (
    <>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="grow">{t('Job roles')}</DialogTitle>
          </DialogHeader>
          <DialogSection>
            <Button
              type="button"
              variant="secondary"
              className="self-start"
              onClick={() => setIsAdding(true)}
            >
              <Plus />
              {t('Add job role')}
            </Button>

            {jobRoles.length === 0 && (
              <Typography className="text-secondary">
                {t('No job roles yet.')}
              </Typography>
            )}

            <ItemGroup>
              {jobRoles.map(jobRole => (
                <Item
                  key={jobRole.id}
                  variant="muted"
                  size="default"
                  className="flex flex-row items-center gap-3"
                >
                  <ItemMedia
                    variant="circle"
                    style={{backgroundColor: jobRole.color}}
                    aria-hidden
                  />
                  <ItemContent>
                    <Typography className="truncate">{jobRole.name}</Typography>
                  </ItemContent>
                  <ItemActions>
                    <TooltipWrapper title={t('Edit job role')}>
                      <IconButton
                        variant="secondary"
                        onClick={() => setEditingJobRole(jobRole)}
                        aria-label={t('Edit job role')}
                      >
                        <Pencil />
                      </IconButton>
                    </TooltipWrapper>
                  </ItemActions>
                </Item>
              ))}
            </ItemGroup>
          </DialogSection>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange?.(false)}
            >
              {t('Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddJobRoleDialog
        open={isAdding}
        onOpenChange={() => setIsAdding(false)}
      />
      <EditJobRoleDialog
        jobRole={editingJobRole || undefined}
        open={!!editingJobRole}
        onOpenChange={() => setEditingJobRole(null)}
      />
    </>
  );
};

export default ManageJobRolesDialog;
